import { createSlice, createAsyncThunk, createAction } from "@reduxjs/toolkit";
import { 
  fetchClients as getClients,
  createClient, 
  updateClient, 
  deleteClient 
} from '../api';

// Update the thunk to use getClients
export const fetchClientsAsync = createAsyncThunk(
  'clients/fetchClients',
  async () => {
    try {
      const response = await getClients();
      return response.data;
    } catch (error) {
      throw error;
    }
  }
);

export const createClientAsync = createAsyncThunk(
  'client/createClient',
  async (clientData, { rejectWithValue }) => {
    try {
      const response = await createClient(clientData);
      return response.data;
    } catch (error) {
      return rejectWithValue(
        error.response?.data?.message || 
        error.response?.data || 
        error.message || 
        'Failed to create client'
      );
    }
  }
);

export const updateClientAsync = createAsyncThunk(
  'client/updateClient',
  async ({ id, updatedClientData }, { rejectWithValue }) => {
    try {
      const response = await updateClient({ id, clientData: updatedClientData });
      
      // If we got a successful response (200-299), return the data even if there was an error later
      if (response.status >= 200 && response.status < 300 && response.data) {
        return response.data;
      }
      
      return response.data;
    } catch (error) {
      // Check if we got a successful response before the error
      // Sometimes the server sends 200 but then throws an error in WebSocket or other async code
      if (error.response?.status === 200 && error.response?.data) {
        return error.response.data; // Return the data as success
      }
      
      // Extract error message from various possible locations
      let errorMessage = 'Failed to update client';
      let errorDetails = null;
      
      if (error.response?.data) {
        if (typeof error.response.data === 'string') {
          errorMessage = error.response.data;
        } else if (error.response.data?.message) {
          errorMessage = error.response.data.message;
        } else if (error.response.data?.error) {
          errorMessage = error.response.data.error;
        } else {
          errorMessage = JSON.stringify(error.response.data);
        }
        errorDetails = error.response.data;
      } else if (error.message) {
        errorMessage = error.message;
      }
      
      // Return error object that will be available in error.payload
      const errorPayload = {
        message: errorMessage,
        status: error.response?.status || 500,
        data: errorDetails,
        originalError: error.message
      };
      
      return rejectWithValue(errorPayload);
    }
  }
);

export const deleteClientAsync = createAsyncThunk(
  'client/deleteClient',
  async (id, { rejectWithValue }) => {
    try {
      const response = await deleteClient(id);
      
      // If we got a successful response (200-299), return the clientId even if there was an error later
      if (response.status >= 200 && response.status < 300) {
        const payload = response?.data;
        const deletedId = payload?.clientId || id;
        return deletedId;
      }
      
      const payload = response?.data;
      const deletedId = payload?.clientId || id;
      return deletedId;
    } catch (error) {
      // Check if we got a successful response before the error
      // Sometimes the server sends 200 but then throws an error in WebSocket or other async code
      if (error.response?.status === 200) {
        const payload = error.response?.data;
        const deletedId = payload?.clientId || id;
        return deletedId; // Return the clientId as success
      }
      
      // Extract error message from various possible locations
      let errorMessage = 'Failed to delete client';
      let errorDetails = null;
      
      if (error.response?.data) {
        if (typeof error.response.data === 'string') {
          errorMessage = error.response.data;
        } else if (error.response.data?.message) {
          errorMessage = error.response.data.message;
        } else if (error.response.data?.error) {
          errorMessage = error.response.data.error;
        } else {
          errorMessage = JSON.stringify(error.response.data);
        }
        errorDetails = error.response.data;
      } else if (error.message) {
        errorMessage = error.message;
      }
      
      // Return error object that will be available in error.payload
      const errorPayload = {
        message: errorMessage,
        status: error.response?.status || 500,
        data: errorDetails,
        originalError: error.message
      };
      
      return rejectWithValue(errorPayload);
    }
  }
);

export const searchClients = createAction('client/searchClients');

const initialState = {
  clients: [],
  filteredClients: [],
  currentClient: null,
  loading: false,
  error: null
};

const clientSlice = createSlice({
  name: 'client',
  initialState,
  reducers: {
    setCurrentClient: (state, action) => {
      state.currentClient = state.clients.find(client => client._id === action.payload);
    },
    clearError: (state) => {
      state.error = null;
    }
  },
  extraReducers: (builder) => {
    builder
      // Get Clients
      .addCase(fetchClientsAsync.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchClientsAsync.fulfilled, (state, action) => {
        state.loading = false;
        state.clients = action.payload;
        state.filteredClients = action.payload;
      })
      .addCase(fetchClientsAsync.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })
      
      // Create Client
      .addCase(createClientAsync.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(createClientAsync.fulfilled, (state, action) => {
        state.loading = false;
        state.clients.push(action.payload);
        state.filteredClients = state.clients;
      })
      .addCase(createClientAsync.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })
      
      // Update Client
      .addCase(updateClientAsync.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(updateClientAsync.fulfilled, (state, action) => {
        state.loading = false;
        state.error = null;
        const updatedClient = action.payload;
        if (updatedClient && updatedClient._id) {
          const index = state.clients.findIndex(client => client._id === updatedClient._id);
          if (index !== -1) {
            state.clients[index] = { ...state.clients[index], ...updatedClient };
          } else {
            // If client not found, add it (shouldn't happen but just in case)
            state.clients.push(updatedClient);
          }
          // Update filtered clients
          const filteredIndex = state.filteredClients.findIndex(client => client._id === updatedClient._id);
          if (filteredIndex !== -1) {
            state.filteredClients[filteredIndex] = { ...state.filteredClients[filteredIndex], ...updatedClient };
          } else {
            state.filteredClients = [...state.clients];
          }
        }
      })
      .addCase(updateClientAsync.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })
      
      // Delete Client
      .addCase(deleteClientAsync.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(deleteClientAsync.fulfilled, (state, action) => {
        state.loading = false;
        state.clients = state.clients.filter(client => client._id !== action.payload);
        state.filteredClients = state.clients;
      })
      .addCase(deleteClientAsync.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })

      // Update the search functionality
      .addCase(searchClients, (state, action) => {
        const searchTerm = action.payload.toLowerCase().trim();
        
        if (!searchTerm) {
          state.filteredClients = state.clients;
          return;
        }

        state.filteredClients = state.clients.filter(client => {
          // Search in name
          const nameMatch = client.name?.toLowerCase().includes(searchTerm);
          
          // Search in phone
          const phoneMatch = client.phone?.toLowerCase().includes(searchTerm);
          
          // Search in email
          const emailMatch = client.email?.toLowerCase().includes(searchTerm);
          
          // Search in address
          const addressMatch = [
            client.address?.street,
            client.address?.number,
            client.address?.floor,
            client.address?.apartment
          ]
            .filter(Boolean)
            .join(' ')
            .toLowerCase()
            .includes(searchTerm);
          
          // Search in description
          const descriptionMatch = client.locationDescription?.toLowerCase().includes(searchTerm);
          
          // Return true if any field matches
          return nameMatch || phoneMatch || emailMatch || addressMatch || descriptionMatch;
        });
      });
  }
});

export const { setCurrentClient, clearError } = clientSlice.actions;
export default clientSlice.reducer; 